import { ColorTranslator } from 'colortranslator';

import { Luma } from './Luma.js';

/**
 * Parses a CSS custom property string to extract the property name.
 * @param {string} prop - The CSS property string (e.g., "var(--my-color)" or "--my-color")
 * @returns {string|undefined} The extracted CSS custom property name (e.g., "--my-color") or undefined if no match
 */
function ParseCssCustomProperty(prop) {
  return prop.match(/^(var\()?(?<prop>--[^\)]*)\)?$/)?.groups.prop;
}

/**
 * Gets the computed value of a CSS custom property from an element or its shadow DOM ancestors.
 * Traverses up the shadow DOM tree until it finds a non-empty value.
 * @param {string} prop - The CSS property string that may contain a custom property
 * @param {Element} elem - The DOM element to start the search from
 * @returns {string|null} The computed custom property value, the original prop if not a custom property, or null if empty
 */
function GetCssCustomProperty(prop, elem) {
  if (typeof prop != 'string') return;
  // See if this color is a custom css property.
  const cssCustomProperty = ParseCssCustomProperty(prop);
  if (cssCustomProperty) {
    let value = null;
    while ((value == null || value == '') && elem != null) {
      value = getComputedStyle(elem).getPropertyValue(cssCustomProperty).trim();
      elem = elem.getRootNode().host;
    }
    if (value == '') return null;
    return value;
  }
  return prop;
}

/**
 * Creates a ColorTranslator instance from a color string, handling CSS variables and special cases.
 * @param {string} color - The color string (can be a CSS color, custom property, or 'transparent')
 * @param {Element} elem - The DOM element used to resolve CSS custom properties
 * @returns {ColorTranslator} A ColorTranslator instance for the resolved color
 */
function GetColorTranslator(color, elem) {
  // Handle 'transparent' color string.
  if (color == 'transparent') color = 'rgba(0,0,0,0)';
  // Convert color (potentially a keyword or css variable) and set default if nullish.
  color = GetCssCustomProperty(color, elem);
  if (color == null || color == '') color = 'white';
  // Return a color translator instance.
  return new ColorTranslator(color);
}

/**
 * Calculates an appropriate text color (light or dark) based on the background color's luminance.
 * Uses the luma value to determine if light or dark text would provide better contrast.
 * @param {string} _color - The background color string
 * @param {Element} elem - The DOM element used to resolve CSS custom properties
 * @returns {string} The calculated text color in RGBA format
 */
function GetTextColor(_color, elem) {
  // Get a color translator.
  const color = GetColorTranslator(_color, elem);
  // Get initial color luma.
  const colorLuma = Luma(color.R, color.G, color.B);
  // Set lightness to be the opposite of what the _color is.
  color.setL(colorLuma < 66.6 ? 98 : 20);
  // Return the color.
  return color.RGBA;
}

/**
 * Calculates an appropriate border color by darkening light colors to improve visibility.
 * Light colors (luma >= 80) have their lightness reduced by 10%, while dark colors remain unchanged.
 * @param {string} _color - The base color string
 * @param {Element} elem - The DOM element used to resolve CSS custom properties
 * @returns {string} The calculated border color in RGBA format
 */
function GetBorderColor(_color, elem) {
  // Get a color translator.
  const color = GetColorTranslator(_color, elem);
  // Get initial color luma.
  const colorLuma = Luma(color.R, color.G, color.B);
  // If color is light, reduce its lightness by 10%.
  if (colorLuma >= 80) color.setL(color.L * 0.9);
  // Return the color.
  return color.RGBA;
}

export { GetColorTranslator, GetTextColor, GetBorderColor };
